import { useState } from "react";
import API from "../lib/api";

const LANGUAGES = ["python", "javascript", "java", "cpp"];

export default function CodeChallenge({ challenge, isDark, t = {}, onSubmitSuccess }) {
  const [code, setCode] = useState(challenge?.starter_code || "");
  const [language, setLanguage] = useState(challenge?.language || "python");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const textPrimary = t.textPrimary || (isDark ? "#ffffff" : "#0a0a0f");
  const textSecondary = t.textSecondary || (isDark ? "rgba(255,255,255,0.5)" : "#64748b");
  const textMuted = t.textMuted || (isDark ? "rgba(255,255,255,0.3)" : "#94a3b8");
  const accent = t.accent || "#f97316";
  const accentDark = t.accentDark || "#ea6a08";
  const accentLight = t.accentLight || (isDark ? "rgba(249,115,22,0.15)" : "rgba(249,115,22,0.08)");
  const accentBorder = t.accentBorder || "rgba(249,115,22,0.25)";

  const difficultyColor = {
    easy: { bg: "rgba(5,150,105,0.12)", color: "#059669" },
    medium: { bg: "rgba(249,115,22,0.12)", color: "#f97316" },
    hard: { bg: "rgba(239,68,68,0.12)", color: "#dc2626" },
  };
  const diff = difficultyColor[challenge?.difficulty] || difficultyColor.medium;

  const editorBg = isDark ? "#0d1117" : "#f6f8fa";
  const editorColor = isDark ? "#e6edf3" : "#1f2328";
  const editorBorder = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)";

  const selectBg = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)";

  async function handleSubmit() {
    if (!challenge?.id) return;
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const res = await API.post(`/api/assessments/challenges/${challenge.id}/submit`, { code, language });
      setResults(res.data);
      if (res.data?.score_pct >= 100 && onSubmitSuccess) onSubmitSuccess(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Submission failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="code-challenge" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: textPrimary, flexGrow: 1 }}>
          {challenge?.title || "Challenge"}
        </h3>
        {challenge?.difficulty && (
          <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: diff.bg, color: diff.color }}>
            {challenge.difficulty}
          </span>
        )}
        {challenge?.language && (
          <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 4, background: accentLight, color: accent, border: `1px solid ${accentBorder}` }}>
            {challenge.language}
          </span>
        )}
      </div>

      {/* Description */}
      {challenge?.description && (
        <div style={{ maxHeight: 120, overflowY: "auto", fontSize: 13, lineHeight: 1.6, color: textSecondary, padding: "10px 12px", borderRadius: 8, background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", border: `1px solid ${editorBorder}` }}>
          {challenge.description}
        </div>
      )}

      {/* Language Selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: textMuted, textTransform: "uppercase", letterSpacing: "0.05em" }}>Language</label>
        <select
          value={language}
          onChange={e => setLanguage(e.target.value)}
          style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6, border: `1px solid ${editorBorder}`, background: selectBg, color: textPrimary, cursor: "pointer" }}
          data-testid="language-selector"
        >
          {LANGUAGES.map(lang => (
            <option key={lang} value={lang}>{lang}</option>
          ))}
        </select>
      </div>

      {/* Code Editor */}
      <textarea
        data-testid="code-editor"
        value={code}
        onChange={e => setCode(e.target.value)}
        spellCheck={false}
        style={{ width: "100%", minHeight: 200, fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace", fontSize: 13, lineHeight: 1.6, padding: "12px 14px", borderRadius: 8, border: `1px solid ${editorBorder}`, background: editorBg, color: editorColor, resize: "vertical", outline: "none", boxSizing: "border-box", tabSize: 2 }}
        placeholder="Write your solution here..."
      />

      {/* Error */}
      {error && (
        <div style={{ fontSize: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", color: "#dc2626" }}>
          {error}
        </div>
      )}

      {/* Submit Button */}
      <button
        data-testid="submit-code-btn"
        onClick={handleSubmit}
        disabled={loading}
        style={{ alignSelf: "flex-end", padding: "8px 24px", borderRadius: 8, border: "none", cursor: loading ? "not-allowed" : "pointer", background: loading ? "rgba(249,115,22,0.5)" : `linear-gradient(135deg, ${accent}, ${accentDark})`, color: "white", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, boxShadow: loading ? "none" : "0 2px 8px rgba(249,115,22,0.35)" }}
      >
        {loading && (
          <span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "white", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
        )}
        {loading ? "Submitting..." : "Submit"}
      </button>

      {/* Results Panel */}
      {results && (
        <div data-testid="results-panel" style={{ borderRadius: 10, border: `1px solid ${editorBorder}`, overflow: "hidden" }}>
          {/* Score header */}
          <div style={{ padding: "12px 16px", background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: textMuted }}>Results</span>
            <span style={{ fontSize: 28, fontWeight: 800, color: results.score_pct >= 80 ? "#059669" : results.score_pct >= 50 ? "#f97316" : "#dc2626", lineHeight: 1 }}>
              {results.score_pct ?? 0}<span style={{ fontSize: 14, fontWeight: 500, color: textSecondary }}>%</span>
            </span>
          </div>

          {/* Test cases */}
          {Array.isArray(results.test_results) && results.test_results.length > 0 && (
            <div style={{ padding: "8px 0" }}>
              {results.test_results.map((tc, i) => (
                <div key={i} style={{ padding: "8px 16px", borderBottom: i < results.test_results.length - 1 ? `1px solid ${editorBorder}` : "none", display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ fontSize: 14, color: tc.passed ? "#059669" : "#dc2626", flexShrink: 0, lineHeight: 1.4 }}>{tc.passed ? "✓" : "✗"}</span>
                  <div style={{ fontSize: 11, color: textSecondary, display: "flex", flexWrap: "wrap", gap: "2px 16px" }}>
                    {tc.input !== undefined && <span><b style={{ color: textMuted }}>Input:</b> <code style={{ fontFamily: "monospace" }}>{String(tc.input)}</code></span>}
                    {tc.expected !== undefined && <span><b style={{ color: textMuted }}>Expected:</b> <code style={{ fontFamily: "monospace" }}>{String(tc.expected)}</code></span>}
                    {tc.actual !== undefined && <span><b style={{ color: textMuted }}>Actual:</b> <code style={{ fontFamily: "monospace", color: tc.passed ? "#059669" : "#dc2626" }}>{String(tc.actual)}</code></span>}
                    {tc.time_ms !== undefined && <span style={{ color: textMuted }}>{tc.time_ms}ms</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
